import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { PencilIcon, KeyIcon, CreditCardIcon, BellIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { getUserProfile, updateUserProfile } from '../lib/db';

// Import API base URL logic at the top of the file after other imports
// API base URL - match the one from Payment.js
const API_BASE_URL = (() => {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5000';
  }
  
  // Check if we're on Vercel deployment
  const isVercel = window.location.hostname.includes('vercel.app') || 
                  window.location.hostname.includes('openorg.fyi');
                  
  if (isVercel) {
    // For Vercel, use the origin
    return window.location.origin;
  }
  
  // Default fallback
  return window.location.origin;
})();

const Account = () => {
  const location = useLocation();
  
  // Check for tab parameter in URL
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const validTabs = ['profile', 'subscription', 'security', 'notifications'];
    return validTabs.includes(tabParam) ? tabParam : 'profile';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const messageTimeoutRef = useRef(null);
  
  // Update active tab when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const validTabs = ['profile', 'subscription', 'security', 'notifications'];
    if (validTabs.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    
    // Show success message if coming from password reset
    if (tabParam === 'security' && !message.text) {
      // Check if this looks like a password reset redirect
      const referrer = document.referrer;
      const isFromAuthRedirect = referrer.includes('/auth-redirect') || 
                                window.location.search.includes('tab=security');
      
      if (isFromAuthRedirect) {
        setMessage({
          type: 'success',
          text: 'Password reset successful! You can now update your password below.'
        });
      }
    }
  }, [location.search, activeTab, message.text]);
  
  const { 
    user, 
    profile, 
    ready,
    refreshUser, 
    hasActiveSubscription, 
    isEmailConfirmed,
    isAuthenticated,
    updatePassword,
    error: authError 
  } = useAuth();
  
  // Fetch user profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        const data = await getUserProfile(user.id);
        
        if (!data) {
          throw new Error('Failed to fetch profile data');
        }
        
        setProfileData(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setMessage({ 
          type: 'error', 
          text: 'Failed to load profile data. Please try again.' 
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfileData();
  }, [user]);
  
  
  // Initialize state
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    email: ''
  });
  
  // Initialize profile form with user data
  useEffect(() => {
    if (user) {
      // Initialize with whatever data we already have from the user object
      // This helps display user info faster before the full profile fetch completes
      setProfileForm(prevForm => ({
        ...prevForm,
        full_name: user.name || user.full_name || user.user_metadata?.full_name || user.user_metadata?.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);
  
  // Update form data when profile data is loaded
  useEffect(() => {
    if (profileData) {
      setProfileForm(prevForm => ({
        ...prevForm,
        full_name: profileData.name || profileData.full_name || '',
        email: user?.email || ''
      }));
    }
  }, [profileData, user]);
  
  // Handle profile form changes
  const handleProfileChange = (e) => {
    setProfileForm({
      ...profileForm,
      [e.target.name]: e.target.value
    });
  };
  
  // Save profile changes
  const handleProfileSubmit = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    setOptimisticError(null);

    const prevProfileData = { ...profileData };
    setProfileData({ ...profileData, full_name: profileForm.full_name }); // Optimistic update

    try {
      const result = await updateUserProfile(user.id, {
        full_name: profileForm.full_name
      });

      if (!result.success) {
        setProfileData(prevProfileData); // Rollback
        setOptimisticError(result.error || 'Failed to update profile. Please try again.');
        setMessage({ type: 'error', text: result.error || 'Failed to update profile. Please try again.' });
        setLoading(false);
        return;
      }

      // Refresh profile data
      const data = await getUserProfile(user.id);
      if (!data) {
        setProfileData(prevProfileData); // Rollback
        setOptimisticError('Failed to refresh profile data.');
        setMessage({ type: 'error', text: 'Failed to refresh profile data.' });
        setLoading(false);
        return;
      }
      setProfileData(data);
      setOptimisticError(null);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      setProfileData(prevProfileData); // Rollback
      setOptimisticError('An unexpected error occurred. Please try again.');
      setMessage({ type: 'error', text: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  
  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);
  
  const tabs = [
    { id: 'profile', name: 'Profile', icon: <PencilIcon className="w-5 h-5" /> },
    { id: 'subscription', name: 'Subscription', icon: <CreditCardIcon className="w-5 h-5" /> },
    { id: 'security', name: 'Security', icon: <KeyIcon className="w-5 h-5" /> },
    { id: 'notifications', name: 'Notifications', icon: <BellIcon className="w-5 h-5" /> }
  ];


  // Compute user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      .split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Get subscription details
  const getSubscriptionDetails = () => {
    if (!profileData) return null;
    
    const isActive = profileData.hasactivesubscription;
    const isCancelled = profileData.subscription_cancelled_at_period_end;
    const plan = profileData.subscriptionplan || 'annual';
    const startDate = profileData.subscriptionstartdate;
    
    // Format plan name for display
    const planName = plan === 'annual' ? 'Annual' : 
                     plan === 'monthly' ? 'Monthly' : 
                     'Professional';
    
    // Determine status based on active and cancellation state
    let status = 'Inactive';
    if (isActive && isCancelled) {
      status = 'Cancelled (Active Until End)';
    } else if (isActive) {
      status = 'Active';
    }
                     
    return {
      isActive,
      isCancelled,
      plan: planName,
      startDate: startDate ? formatDate(startDate) : 'Unknown',
      status,
      renewalDate: startDate ? 
        formatDate(new Date(new Date(startDate).setFullYear(new Date(startDate).getFullYear() + 1))) : 
        'Unknown',
      price: plan === 'annual' ? '£120/year' : '£12/month',
      features: [
        "Unlimited access to all Open Org playbooks",
        "Access to all new playbook releases",
        "Exclusive HR tech discounts",
        "Early access to new features"
      ]
    };
  };
  
  // Open Stripe customer portal
  const openStripePortal = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    // Make sure we have a stripe_customer_id
    if (!profileData?.stripe_customer_id) {
      setMessage({
        type: 'error',
        text: 'No Stripe customer ID found. Please contact support.'
      });
      setLoading(false);
      return;
    }
    
    try {
      // Create a portal session - simpler version following checkout pattern
      const response = await fetch(`${API_BASE_URL}/api/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: profileData.stripe_customer_id,
          returnUrl: window.location.origin + '/account',
        }),
        // Add a timeout to prevent hanging requests
        signal: AbortSignal.timeout(15000) // 15 second timeout
      });
      
      // If not OK, handle error
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || `Server error: ${response.status}`);
      }
      
      // Parse JSON response
      const data = await response.json();
      
      if (!data || !data.url) {
        throw new Error('No portal URL returned');
      }
      
      // Success! Redirect to the portal
      window.location.href = data.url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      
      // Special handling for AbortError (timeout)
      if (error.name === 'AbortError') {
        setMessage({
          type: 'error',
          text: 'Request timed out. The server may be unavailable.'
        });
      } else {
        setMessage({
          type: 'error',
          text: error.message || 'Failed to open billing portal. Please try again later.'
        });
      }
      
      setLoading(false);
    }
  };

  // Handle security tab and password change
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Handle password form changes
  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
    
    // Clear errors when user types
    if (passwordErrors[e.target.name]) {
      setPasswordErrors({
        ...passwordErrors,
        [e.target.name]: ''
      });
    }
  };
  
  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};
    
    // Note: We don't validate current password since Supabase handles authentication
    // through the user's active session
    
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordForm.newPassword)) {
      errors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return errors;
  };
  
  // Update password
  const handlePasswordSubmit = async () => {
    const errors = validatePasswordForm();
    
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    setPasswordLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const { success, error } = await updatePassword(passwordForm.newPassword);
      
      if (!success) {
        throw new Error(error?.message || 'Failed to update password');
      }
      
      // Reset form
      setPasswordForm({
        newPassword: '',
        confirmPassword: ''
      });
      
      // Clear any validation errors
      setPasswordErrors({});
      
      setMessage({
        type: 'success',
        text: 'Password updated successfully! Please use your new password for future sign-ins.'
      });
      
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to update password. Please try again.'
      });
    } finally {
      setPasswordLoading(false);
    }
  };
  
  // Determine if user is using OAuth (Google, etc.)
  const isOAuthUser = () => {
    return user?.app_metadata?.provider && 
           user.app_metadata.provider !== 'email';
  };

  // Handle notifications tab
  const [notificationSettings, setNotificationSettings] = useState({
    content_updates: true,
    discount_alerts: true,
    subscription_updates: true,
    marketing_emails: false,
    frequency: 'weekly'
  });
  
  // Update form data when profile data is loaded
  useEffect(() => {
    if (profileData && profileData.notification_preferences) {
      try {
        // If stored as JSON string, parse it
        const preferences = typeof profileData.notification_preferences === 'string' 
          ? JSON.parse(profileData.notification_preferences)
          : profileData.notification_preferences;
          
        setNotificationSettings(prev => ({
          ...prev,
          ...preferences
        }));
      } catch (error) {
        console.error('Error parsing notification preferences:', error);
      }
    }
  }, [profileData]);
  
  // Handle notification toggle
  const handleNotificationToggle = (key) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Handle frequency change
  const handleFrequencyChange = (frequency) => {
    setNotificationSettings(prev => ({
      ...prev,
      frequency
    }));
  };
  
  // Save notification preferences
  const saveNotificationPreferences = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          notification_preferences: notificationSettings,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
        
      if (error) throw error;
      
      setMessage({
        type: 'success',
        text: 'Notification preferences saved successfully!'
      });
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      setMessage({
        type: 'error',
        text: 'Failed to save notification preferences. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Generic profile picture URL
  const genericProfilePicUrl = "https://ui-avatars.com/api/?background=0D8ABC&color=fff";

  // Get member registration date
  const getMemberSince = () => {
    if (!profileData) return 'Unknown';
    
    // Specifically use subscription start date instead of other date fields
    const dateField = profileData.subscriptionstartdate;
    
    if (!dateField) {
      return 'Unknown';
    }
    
    try {
      const formattedDate = new Date(dateField).toLocaleDateString();
      return formattedDate;
    } catch (error) {
      console.error("Error formatting subscriptionstartdate:", error);
      return 'Unknown';
    }
  };

  // Ensure subscriptionstartdate is set if user has an active subscription
  useEffect(() => {
    // Skip if no profile data or already has a subscription start date
    if (!profileData || !user || profileData.subscriptionstartdate) return;
    
    // If user has active subscription but no start date, set one
    if (profileData.hasactivesubscription) {
      
      // Create a date for the 1st of the current month as a reasonable default
      const today = new Date();
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      
      // Update the profile with a subscription start date
      (async () => {
        try {
          const { error } = await supabase
            .from('profiles')
            .update({
              subscriptionstartdate: firstOfMonth.toISOString()
            })
            .eq('id', user.id);
            
          if (error) {
            console.error("Error setting default subscription start date:", error);
          } else {
            // Refresh the user's profile data to get the updated subscription start date
            const refreshedProfile = await getUserProfile(user.id);
            if (refreshedProfile) {
              setProfileData(refreshedProfile);
            }
          }
        } catch (err) {
          console.error("Exception setting default subscription start date:", err);
        }
      })();
    }
  }, [profileData, user]);

  // Add Toast component
  const Toast = ({ message }) => {
    if (!message.text) return null;
    
    return (
      <div 
        className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-md shadow-md transition-all duration-300 transform ${
          message.text ? 'translate-y-0 opacity-100' : 'translate-y-[-20px] opacity-0 pointer-events-none'
        } ${
          message.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}
      >
        <div className="flex items-center gap-2">
          {message.type === 'error' ? (
            <ExclamationCircleIcon className="w-5 h-5" />
          ) : (
            <CheckCircleIcon className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{message.text}</span>
          <button 
            onClick={() => setMessage({ type: '', text: '' })}
            className="ml-2 text-white/80 hover:text-white"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  const [optimisticError, setOptimisticError] = useState(null);

  return (
    <div className="w-full mx-auto max-w-6xl">
      <h1 className="text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8">Account Settings</h1>
      
      {/* Toast Notification */}
      <Toast message={message} />
      
      {/* Loading State */}
      {loading && !profileData && (
        <div className="bg-gray-900 rounded-lg p-8 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em]"></div>
          <p className="mt-4 text-gray-400">Loading your account information...</p>
        </div>
      )}
      
      {/* Content (Only shown when not initially loading) */}
      {(!loading || profileData) && (
        <>
      {/* Tabs */}
      <div className="flex flex-wrap border-b border-gray-800 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`flex items-center mr-4 sm:mr-6 pb-3 px-1 text-sm font-medium border-b-2 ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-400 hover:text-white hover:border-gray-700'
            }`}
            onClick={() => {
              setActiveTab(tab.id);
              // Update URL without causing a page reload
              const newUrl = new URL(window.location);
              if (tab.id === 'profile') {
                // Remove tab parameter for default tab to keep URL clean
                newUrl.searchParams.delete('tab');
              } else {
                newUrl.searchParams.set('tab', tab.id);
              }
              window.history.replaceState({}, '', newUrl);
            }}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.name}
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div className="bg-gray-900 rounded-lg shadow-lg">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
              <div className="p-4 sm:p-6 relative">
                {/* Overlay loading state for form submissions */}
                {loading && (
                  <div className="absolute inset-0 bg-gray-900/70 flex items-center justify-center rounded-lg z-10">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em]"></div>
                  </div>
                )}
                
            <div className="flex flex-col sm:flex-row sm:items-center mb-6 pb-6 border-b border-gray-800">
              <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-6">
                    {profileData?.avatar_url ? (
                      <img 
                        src={profileData.avatar_url} 
                        alt={profileForm.full_name} 
                        className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover"
                  />
                ) : (
                      <div className="relative h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-primary flex items-center justify-center overflow-hidden">
                        {profileForm.full_name ? (
                          <span className="text-white text-xl sm:text-2xl font-medium">
                            {getUserInitials(profileForm.full_name)}
                          </span>
                        ) : (
                          <img 
                            src={genericProfilePicUrl} 
                            alt="Profile" 
                            className="h-full w-full object-cover"
                          />
                        )}
                  </div>
                )}
              </div>
              <div className="flex-1">
                    <h2 className="text-lg sm:text-xl font-semibold text-white mb-1">{profileForm.full_name || 'User'}</h2>
                    <p className="text-gray-400 text-sm mb-3">{profileForm.email}</p>
                {/* <button className="text-xs font-medium bg-transparent hover:bg-gray-800 text-primary border border-primary py-1.5 px-3 rounded-md transition-colors">
                  Change Profile Photo
                </button> */}
              </div>
            </div>
            
            {optimisticError && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-md text-red-300 text-sm">
                {optimisticError}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-xs text-gray-500 uppercase font-medium mb-2">Full Name</label>
                <input 
                  type="text" 
                  className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  name="full_name"
                  value={profileForm.full_name}
                  onChange={handleProfileChange}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 uppercase font-medium mb-2">Email</label>
                <input 
                  type="email" 
                  className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-md text-gray-400 focus:outline-none"
                  name="email"
                  value={profileForm.email}
                  disabled
                  title="Email cannot be changed. Contact support for assistance."
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 uppercase font-medium mb-2">Member Since</label>
                <input 
                  type="text" 
                  className="w-full py-2 px-3 bg-gray-800 border border-gray-700 rounded-md text-gray-400 focus:outline-none"
                  value={getMemberSince()}
                  disabled
                />
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
                  <button 
                    className="font-medium bg-primary hover:bg-primary-dark text-white py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
                    onClick={handleProfileSubmit}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            
            {/* Debugging section - can be removed in production */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-8 border-t border-gray-700 pt-4">
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-400 font-medium">Debug: Profile Data</summary>
                  <div className="mt-2 p-2 bg-gray-800 rounded overflow-x-auto">
                    <pre className="text-gray-400">
                      {JSON.stringify({
                        ...profileData,
                        _special_fields: {
                          subscriptionstartdate: {
                            raw: profileData?.subscriptionstartdate,
                            typeof: typeof profileData?.subscriptionstartdate,
                            exists: profileData?.hasOwnProperty('subscriptionstartdate'),
                            date: profileData?.subscriptionstartdate ? new Date(profileData.subscriptionstartdate).toString() : null
                          },
                          memberSince: getMemberSince()
                        }
                      }, null, 2)}
                    </pre>
                  </div>
                </details>
              </div>
            )}
          </div>
        )}
        
        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
              <div className="p-4 sm:p-6 relative">
                {/* Overlay loading state */}
                {loading && (
                  <div className="absolute inset-0 bg-gray-900/70 flex items-center justify-center rounded-lg z-10">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em]"></div>
                  </div>
                )}
                
                {profileData?.hasactivesubscription ? (
                  <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 pb-6 border-b border-gray-800">
              <div>
                <div className="flex items-center">
                          <h2 className="text-lg sm:text-xl font-semibold text-white">{getSubscriptionDetails()?.plan} Plan</h2>
                  <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    getSubscriptionDetails()?.isCancelled 
                      ? 'bg-orange-500/20 text-orange-400' 
                      : 'bg-success/20 text-success'
                  }`}>
                            {getSubscriptionDetails()?.status}
                  </span>
                </div>
                        <p className="text-gray-400 text-sm mt-1">Annual billing • {getSubscriptionDetails()?.price}</p>
                        {getSubscriptionDetails()?.isCancelled && (
                          <p className="text-orange-400 text-sm mt-1">
                            Your subscription will not renew after {getSubscriptionDetails()?.renewalDate}
                          </p>
                        )}
              </div>
              <div className="mt-4 sm:mt-0">
                        <button 
                          className="text-xs font-medium bg-primary hover:bg-primary-dark text-white py-1.5 px-3 rounded-md transition-colors disabled:opacity-50"
                          onClick={openStripePortal}
                          disabled={loading || !profileData?.stripe_customer_id}
                        >
                  Manage Billing
                </button>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-white mb-3">Plan Features</h3>
              <ul className="space-y-2">
                        {getSubscriptionDetails()?.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-success mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center mb-2">
                        <h3 className="text-sm font-medium text-white">Subscription Details</h3>
                        {profileData?.stripe_subscription_id && (
                          <span className="text-xs text-gray-400">ID: {profileData.stripe_subscription_id.substring(0, 10)}...</span>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-400">Status:</span>
                          <span className={`text-xs font-medium ${
                            getSubscriptionDetails()?.isCancelled 
                              ? 'text-orange-400' 
                              : 'text-success'
                          }`}>
                            {getSubscriptionDetails()?.status}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-400">Start Date:</span>
                          <span className="text-xs text-gray-300">{getSubscriptionDetails()?.startDate}</span>
              </div>
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-400">
                            {getSubscriptionDetails()?.isCancelled ? 'Access Until:' : 'Renewal Date:'}
                          </span>
                          <span className="text-xs text-gray-300">{getSubscriptionDetails()?.renewalDate}</span>
                </div>
                        {getSubscriptionDetails()?.isCancelled && (
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-400">Auto-Renewal:</span>
                            <span className="text-xs text-orange-400">Disabled</span>
                          </div>
                        )}
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-sm font-medium text-white">Billing Management</h3>
                      </div>
                      <p className="text-xs text-gray-400 mb-4">
                        You can manage your payment methods, view invoices, cancel your subscription and update billing information through our secure Stripe portal.
                      </p>
                      <button 
                        onClick={openStripePortal}
                        disabled={loading || !profileData?.stripe_customer_id}
                        className="w-full flex justify-center items-center py-2 px-4 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Open Stripe Customer Portal
                      </button>
                      {!profileData?.stripe_customer_id && (
                        <p className="text-xs text-red-400 mt-2">
                          No Stripe customer ID found. Please contact support.
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="mx-auto w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                      <CreditCardIcon className="w-8 h-8 text-gray-500" />
              </div>
                    <h3 className="text-xl font-medium text-white mb-2">No Active Subscription</h3>
                    <p className="text-gray-400 text-sm mb-6 max-w-md mx-auto">
                      You currently don't have an active subscription. Subscribe to get access to all our premium content and features.
                    </p>
                    <button 
                      onClick={() => window.location.href = '/payment'}
                      className="px-6 py-2 bg-primary hover:bg-primary-dark text-white font-medium rounded-md transition-colors"
                    >
                      Subscribe Now
                </button>
                  </div>
                )}
          </div>
        )}
        
        {/* Security Tab */}
        {activeTab === 'security' && (
              <div className="p-4 sm:p-6 relative">
                {/* Overlay loading state */}
                {passwordLoading && (
                  <div className="absolute inset-0 bg-gray-900/70 flex items-center justify-center rounded-lg z-10">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em]"></div>
                  </div>
                )}
                
                {isOAuthUser() ? (
                  // OAuth user security options
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                        <span className="text-white font-bold">G</span>
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-white">Google Authentication</h2>
                        <p className="text-sm text-gray-400">You're signed in with Google</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-800 p-4 rounded-lg mb-6">
                      <p className="text-sm text-gray-300 mb-4">
                        Your account is secured with Google authentication. To change your password, please visit your Google account settings.
                      </p>
                      <a 
                        href="https://myaccount.google.com/security" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:text-primary-light"
                      >
                        Manage Google Account Security →
                      </a>
                    </div>
                    
                    <div className="border-t border-gray-800 pt-6 mt-6">
                      <h2 className="text-lg font-semibold text-white mb-4">Sign In Options</h2>
                      <p className="text-sm text-gray-400 mb-4">
                        You can only sign in with your Google account. If you'd like to add a password or change your sign-in method, please contact support.
                      </p>
                    </div>
                  </div>
                ) : (
                  // Email user security options
                  <div>
            <h2 className="text-lg font-semibold text-white mb-4">Change Password</h2>
            <p className="text-sm text-gray-400 mb-4">
              Enter a new password for your account. Your session will remain active after the password change.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); handlePasswordSubmit(); }} className="space-y-4">
              {/* Hidden username field for accessibility */}
              <input
                type="text"
                name="username"
                value={user?.email || ''}
                autoComplete="username"
                style={{ display: 'none' }}
                readOnly
              />
              <div>
                <label className="block text-xs text-gray-500 uppercase font-medium mb-2">New Password</label>
                <input 
                  type="password" 
                          name="newPassword"
                          autoComplete="new-password"
                          className={`w-full py-2 px-3 bg-gray-800 border ${passwordErrors.newPassword ? 'border-red-500' : 'border-gray-700'} rounded-md text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
                  placeholder="Enter new password"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordChange}
                />
                        {passwordErrors.newPassword && (
                          <p className="mt-1 text-xs text-red-400">{passwordErrors.newPassword}</p>
                        )}
              </div>
              <div>
                <label className="block text-xs text-gray-500 uppercase font-medium mb-2">Confirm Password</label>
                <input 
                  type="password" 
                          name="confirmPassword"
                          autoComplete="new-password"
                          className={`w-full py-2 px-3 bg-gray-800 border ${passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-700'} rounded-md text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
                  placeholder="Confirm new password"
                          value={passwordForm.confirmPassword}
                          onChange={handlePasswordChange}
                />
                        {passwordErrors.confirmPassword && (
                          <p className="mt-1 text-xs text-red-400">{passwordErrors.confirmPassword}</p>
                        )}
              </div>
            
            <div className="mt-6 mb-8">
                      <button 
                        type="submit"
                        className="font-medium bg-primary hover:bg-primary-dark text-white py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={passwordLoading}
                      >
                        {passwordLoading ? 'Updating Password...' : 'Update Password'}
              </button>
            </div>
            </form>
            
            <div className="border-t border-gray-800 pt-6">
                      <h2 className="text-lg font-semibold text-white mb-4">Account Recovery</h2>
                      <p className="text-sm text-gray-400 mb-4">
                        Make sure your account recovery options are up to date. If you lose access to your account, we'll use these to help you recover it.
                      </p>
                      <div className="flex items-center mb-2">
                        <div className="w-4 h-4 mr-2 bg-green-500 rounded-full flex-shrink-0"></div>
                        <span className="text-sm text-gray-300">Email: {user?.email}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* 2FA section - for both types of users */}
                <div className="border-t border-gray-800 pt-6 mt-6">
              <h2 className="text-lg font-semibold text-white mb-4">Two-Factor Authentication</h2>
              <p className="text-sm text-gray-400 mb-4">Add an extra layer of security to your account</p>
                  <button 
                    className="font-medium bg-gray-800 hover:bg-gray-700 text-white py-2 px-4 rounded-md transition-colors"
                    onClick={() => setMessage({
                      type: 'info',
                      text: 'Two-factor authentication is coming soon!'
                    })}
                  >
                Enable 2FA
              </button>
                  <p className="text-xs text-gray-500 mt-2">
                    This feature is coming soon. Check back later!
                  </p>
            </div>
          </div>
        )}
        
        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
              <div className="p-4 sm:p-6 relative">
                {/* Overlay loading state */}
                {loading && (
                  <div className="absolute inset-0 bg-gray-900/70 flex items-center justify-center rounded-lg z-10">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em]"></div>
                  </div>
                )}
                
            <h2 className="text-lg font-semibold text-white mb-4">Notification Preferences</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-800">
                <div>
                  <h3 className="text-sm font-medium text-white">New Content Updates</h3>
                  <p className="text-xs text-gray-400 mt-1">Get notified when new playbooks or courses are available</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={notificationSettings.content_updates}
                        onChange={() => handleNotificationToggle('content_updates')}
                      />
                  <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-gray-800">
                <div>
                  <h3 className="text-sm font-medium text-white">Discount Alerts</h3>
                  <p className="text-xs text-gray-400 mt-1">Be the first to know about new discounts and offers</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={notificationSettings.discount_alerts}
                        onChange={() => handleNotificationToggle('discount_alerts')}
                      />
                  <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-gray-800">
                <div>
                  <h3 className="text-sm font-medium text-white">Subscription Updates</h3>
                  <p className="text-xs text-gray-400 mt-1">Receive notifications about your subscription status</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={notificationSettings.subscription_updates}
                        onChange={() => handleNotificationToggle('subscription_updates')}
                      />
                  <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between py-3 border-b border-gray-800">
                <div>
                  <h3 className="text-sm font-medium text-white">Marketing Emails</h3>
                  <p className="text-xs text-gray-400 mt-1">Receive promotional content and marketing materials</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={notificationSettings.marketing_emails}
                        onChange={() => handleNotificationToggle('marketing_emails')}
                      />
                  <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="text-sm font-medium text-white mb-3">Email Notification Frequency</h3>
                  <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center">
                      <input 
                        type="radio" 
                        name="frequency" 
                        className="h-4 w-4 border-gray-700 text-primary focus:ring-primary focus:ring-offset-gray-900" 
                        checked={notificationSettings.frequency === 'daily'}
                        onChange={() => handleFrequencyChange('daily')}
                      />
                  <span className="ml-2 text-sm text-gray-300">Daily</span>
                </label>
                <label className="inline-flex items-center">
                      <input 
                        type="radio" 
                        name="frequency" 
                        className="h-4 w-4 border-gray-700 text-primary focus:ring-primary focus:ring-offset-gray-900" 
                        checked={notificationSettings.frequency === 'weekly'}
                        onChange={() => handleFrequencyChange('weekly')}
                      />
                  <span className="ml-2 text-sm text-gray-300">Weekly</span>
                </label>
                <label className="inline-flex items-center">
                      <input 
                        type="radio" 
                        name="frequency" 
                        className="h-4 w-4 border-gray-700 text-primary focus:ring-primary focus:ring-offset-gray-900" 
                        checked={notificationSettings.frequency === 'monthly'}
                        onChange={() => handleFrequencyChange('monthly')}
                      />
                  <span className="ml-2 text-sm text-gray-300">Monthly</span>
                </label>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
                  <button 
                    className="font-medium bg-primary hover:bg-primary-dark text-white py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={saveNotificationPreferences}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default Account; 